import React, { useCallback } from 'react';
import { TagsContainer, Tag, PlusChip } from '../styled';
import LibraryTooltip from 'components/Tooltip';
import { AutocompleteStyles, TagCalculation } from '../types';

interface TagsProps {
  tagCalculation: TagCalculation | null;
  value?: string[];
  onRemoveTag: (index: number) => void;
  small?: boolean;
  styles?: AutocompleteStyles;
  disabled?: boolean; // Add disabled prop
}

export const Tags: React.FC<TagsProps> = React.memo(({
  tagCalculation,
  value,
  onRemoveTag,
  small,
  styles,
  disabled
}) => {
  const createTagClickHandler = useCallback(
    (option: string) => {
      return () => {
        if (disabled) return; // Prevent click when disabled
        const index = value?.indexOf(option) ?? -1;
        if (index !== -1) {
          onRemoveTag(index);
        }
      };
    },
    [value, onRemoveTag, disabled], // Add disabled to dependencies
  );

  const handleTagKeyDown = useCallback(
    (option: string, event: React.KeyboardEvent) => {
      if (disabled) return; // Prevent keyboard interaction when disabled
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const index = value?.indexOf(option) ?? -1;
        if (index !== -1) {
          onRemoveTag(index);
        }
      }
    },
    [value, onRemoveTag, disabled], // Add disabled to dependencies
  );

  if (!tagCalculation) return null;

  const { sortedValue, limitTags, numTags } = tagCalculation;

  return (
    <TagsContainer style={styles?.tagsContainer}>
      {sortedValue.slice(0, limitTags).map((option, index) => (
        <Tag
          key={`${option}-${index}`}
          isFirst={index === 0}
          onClick={createTagClickHandler(option)}
          onKeyDown={(e) => handleTagKeyDown(option, e)}
          tabIndex={disabled ? -1 : 0} // Remove from tab order when disabled
          role="button"
          aria-label={`Remove ${option}`}
          aria-disabled={disabled} // Add aria-disabled
          style={{
            ...styles?.tag,
            cursor: disabled ? 'default' : 'pointer', // Change cursor when disabled
            opacity: disabled ? 0.6 : 1, // Visual indication when disabled
          }}
          small={small}
          disabled={disabled}
        >
          {option}
          {index < Math.min(limitTags, numTags) - 1 && ','}
        </Tag>
      ))}
      {numTags > limitTags && (
        <LibraryTooltip text={disabled ? '' : value?.slice(limitTags).join(', ') ?? ''}>
          <PlusChip
            style={{
              ...styles?.plusChip,
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
            hasMargin={limitTags > 0}
            tabIndex={disabled ? -1 : 0}
            role="button"
            aria-label={`${numTags - limitTags} more selected options`}
            aria-disabled={disabled}
            small={small}
          >
            +{numTags - limitTags}
          </PlusChip>
        </LibraryTooltip>
      )}
    </TagsContainer>
  );
});
